<template>
    <fieldset class="bg-white shadow-md px-4 py-8 rounded-lg" :class="$attrs['class']">
        <expand-transition :duration="300">
            <div
                key="status"
                v-show="details.verified || details.verifying"
                class="flex w-full bank-verification flex-no-wrap"
            >
                <div class="flex left flex-grow-0 justify-center items-center relative">
                    <img
                        :class="{ 'scale-0': details.verified, 'scale-100': !details.verified }"
                        class="transform absolute transition-transform duration-300 origin-center"
                        width="50"
                        height="50"
                        src="/img/tail-spin.svg?v=1"
                    />
                    <svg
                        :class="{ 'scale-0': !details.verified, 'scale-100': details.verified }"
                        class="transform absolute transition-transform duration-300 origin-center easing-overshoot"
                        width="50"
                        height="50"
                        xmlns="http://www.w3.org/2000/svg"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                    >
                        <defs>
                            <filter
                                x="-4.3%"
                                y="-1.6%"
                                width="108.5%"
                                height="103.7%"
                                filterUnits="objectBoundingBox"
                                id="a"
                            >
                                <feOffset dy="2" in="SourceAlpha" result="shadowOffsetOuter1" />
                                <feGaussianBlur
                                    stdDeviation="5"
                                    in="shadowOffsetOuter1"
                                    result="shadowBlurOuter1"
                                />
                                <feColorMatrix
                                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"
                                    in="shadowBlurOuter1"
                                />
                            </filter>
                            <filter
                                x="-5.7%"
                                y="-18.9%"
                                width="111.3%"
                                height="142.2%"
                                filterUnits="objectBoundingBox"
                                id="c"
                            >
                                <feOffset dy="2" in="SourceAlpha" result="shadowOffsetOuter1" />
                                <feGaussianBlur
                                    stdDeviation="6"
                                    in="shadowOffsetOuter1"
                                    result="shadowBlurOuter1"
                                />
                                <feColorMatrix
                                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"
                                    in="shadowBlurOuter1"
                                />
                            </filter>
                            <linearGradient x1="74.393%" y1="91.975%" x2="-3.605%" y2="-8.917%" id="e">
                                <stop stop-color="#4CB8C4" offset="0%" />
                                <stop stop-color="#3CD3AD" offset="100%" />
                            </linearGradient>
                            <rect id="d" x="20" y="152" width="335" height="90" rx="6" />
                            <path id="b" d="M0-39h375v856H0z" />
                        </defs>
                        <g fill="none" fill-rule="evenodd">
                            <g transform="translate(-40 -172)">
                                <use fill="#000" filter="url(#a)" xlink:href="#b" />
                                <use fill="#F7F7F7" xlink:href="#b" />
                            </g>
                            <g transform="translate(-40 -172)">
                                <use fill="#000" filter="url(#c)" xlink:href="#d" />
                                <use fill="#FFF" xlink:href="#d" />
                            </g>
                            <g>
                                <circle fill="url(#e)" cx="25" cy="25" r="21" />
                                <path
                                    stroke="#FFF"
                                    stroke-width="1.818"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M18 25.665L23.221 31 34 20"
                                />
                                <circle stroke="url(#e)" stroke-width="2" cx="25" cy="25" r="24" />
                            </g>
                        </g>
                    </svg>
                </div>

                <div class="flex right flex-col px-2 flex-1" v-show="details.verifying" key="verifyingText">
                    <div class="flex w-full text-green text-xl justify-center font-bold">Validating...</div>
                    <div class="flex w-full justify-center">Your details are being validated</div>
                </div>

                <div class="flex right flex-col px-2 flex-1" v-show="details.verified" key="verifiedText">
                    <div
                        class="flex w-full text-green text-xl justify-center font-bold"
                    >Bank details validated</div>
                    <div
                        class="flex w-full justify-center text-center"
                    >Your details were validated: {{ this.today }}</div>
                </div>
            </div>
        </expand-transition>

        <expand-transition :duration="300">
            <div v-show="!details.verified && !details.verifying">
                <!-- <div key="inputs"> -->
                <ValidationObserver
                    slim
                    :customer="customer"
                    ref="validationObserver"
                >
                    <digi-sortcode
                        :name="this.name + '.sort_code'"
                        class="mb-8"
                        label="Sort code"
                        :rules="form.getValidationRules(name + '.sort_code')"
                        :custom-messages="form.getValidationMessages(name + '.sort_code')"
                        :value="details.sort_code"
                        @input="update('sort_code', $event)"
                    ></digi-sortcode>

                    <digi-input
                        type="tel"
                        label="Account number"
                        v-model="details.account_number"
                        :name="this.name + '.account_number'"
                        :attributes="{ maxlength: 8, inputmode: 'tel' }"
                        :rules="form.getValidationRules(name + '.account_number')"
                        :custom-messages="form.getValidationMessages(name + '.account_number')"
                        microtext="This cannot be used to take repayments from you, <strong>this is for validation</strong>"
                        v-number-only="true"
                        @input="update('account_number', $event)"
                        @prefix_account_number="prefixAccountNumber"
                        :input-classes="{ 'font-password': concealed }"
                        v-bind:concealed="concealed"
                    >
                        <div class="absolute right-0 top-0 h-full flex items-center mr-3" style="width: 33px;">
                            <fade-transition :duration="1000" group>
                                <img src="/img/conceal.svg" v-if="!concealed" height="33" width="33" class="" @click="concealed = !concealed">
                                <img src="/img/conceal-alt.svg" v-if="concealed" height="20" width="33" class="" @click="concealed = !concealed">
                            </fade-transition>
                        </div>
                    </digi-input>

                    <fancy-select
                        :name="this.name + '.debit_card'"
                        label="Which debit card do you have?"
                        v-model="details.debit_card"
                        :rules="form.getValidationRules(name + '.debit_card')"
                        :custom-messages="form.getValidationMessages(name + '.debit_card')"
                        :options="debitCardOptions"
                        @input="update('debit_card', $event)"
                        :focus-on-next="false"
                        tooltip="This is just for confirmation - you need a valid UK debit card to be eligible for a loan with us."
                    ></fancy-select>
                </ValidationObserver>
            </div>
        </expand-transition>
    </fieldset>
</template>

<script>
import DigiInput from "./../../components/form/digi-input";
import DigiSortCode from "./../../components/form/digi-sortcode";
import FancySelect from "./../../components/form/fancyselect";
import { debounce } from "debounce";
import { scrollToElementIfRequired } from '../../assets/js/form';
import { cloneDeep, tap, set } from 'lodash';
import ExpandTransition from './../../components/expand-transition';
import FadeTransition from './../../components/fade-transition';
const moment = require("moment");

export default {
    data() {
        return {
            concealed: true
        }
    },
    props: { 
        'value': {},
        "customer": {
            default: () => ({})
        },
        "form": Object,
        "debitCardOptions": {
            default: () => ({})
        },
        "name": String
    },
    computed: {
        details() {
            return this.value ? this.value : {
                account_number: null,
                sort_code: null,
                debit_card: 'visa_debit',
                verified: false,
                verifying: false,
            };
        },
        today() {
            return moment().format("DD/MM/YYYY");
        },
        inputType() {
            return this.concealed ? 'password' : 'tel';
        }
    },
    methods: {
        prefixAccountNumber() {
            this.details.account_number = ('0' + this.details.account_number);
        },
        update(key, value) {
            this.$emit('input', tap(cloneDeep(this.details), v => set(v, key, value)));
            this.checkAllValid();
        },
        checkAllValid: debounce(function(e) {
            if (this.details.verifying || this.details.verified) {
                return;
            }
            let observer = this.$refs.validationObserver;
            observer.validate({ silent: true, }).then(validated => {
                if (validated) {
                    this.details.verifying = true;
                    scrollToElementIfRequired(this.$el);
                    setTimeout(() => {
                        this.details.verifying = false;
                        this.details.verified = true;
                    }, 2000);
                }
            });
        }, 1000)
    },
    components: {
        "digi-sortcode": DigiSortCode,
        "digi-input": DigiInput,
        "fancy-select": FancySelect,
        'expand-transition': ExpandTransition,
        'fade-transition': FadeTransition,
    }
};
</script>

<style lang="scss" scoped>
.bank-verification {
    .left {
        width: 66px;
        height: 66px;
    }
    .rotate {
        animation: rotate-anim 2s infinite cubic-bezier(0.165, 0.84, 0.44, 1);
        // animation: rotate-anim 2s infinite cubic-bezier(0.075, 0.82, 0.165, 1);
    }
}
.easing-overshoot {
    transition-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1.375);
}
@keyframes rotate-anim {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>